import { Component, OnInit, Input, OnChanges, SimpleChanges, Output, EventEmitter, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { ElementRef, ViewChild } from '@angular/core';
import { CommonModule, DatePipe, registerLocaleData } from '@angular/common';
import { FormsModule } from '@angular/forms';
import localeEs from '@angular/common/locales/es';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

// Registrar locale 'es' para DatePipe cuando este componente se cargue
try {
  registerLocaleData(localeEs, 'es');
} catch (e) {
  // ignore if already registered
}
import { LastPosicion } from '/app/mapaRiesgo/models/lastposicion';
import { LastposicionMonitorComponent } from '../lastposicionMonitor/lastposicionMonitor.component';


@Component({
  selector: 'app-lastposicion',
  standalone: true,
  imports: [CommonModule, LastposicionMonitorComponent, MatSlideToggleModule, FormsModule],
  providers: [DatePipe],
  templateUrl: './lastposicion.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./lastposicion.component.css']
})
export class LastposicionComponent implements OnInit, OnChanges {
  @Input() lastPositions: any[] = [];
  @Input() alarms: any[] = [];
  @Output() filteredAssetIdsChange = new EventEmitter<string[] | null>();
  // Mantener la Input para compatibilidad de template, pero IGNORAR actualizaciones entrantes
  private _clusteringEnabledInternal: boolean = false; // inicia inactivo
  @Input()
  set clusteringEnabled(_val: boolean) {
    // intentionally ignore parent updates to let this component control its own toggle state
  }
  get clusteringEnabled(): boolean { return this._clusteringEnabledInternal; }
  @Output() toggleClusteringEvent = new EventEmitter<boolean>();
  @Input() alarmsByAssetId: { [key: string]: any[] } = {};
  @Input() isRiskEvaluation: boolean = false;
  @Input() newAlarmAssetIds: Set<string> | null = null;
  @Output() vehicleSelected = new EventEmitter<{ latitude: number; longitude: number; assetName: string; assetId: string }>();
  
  // Propiedades de filtrado
  filterText: string = '';
  showFilters: boolean = false;
  filteredAlarms: any[] = [];
  
  columns: string[] = [];
  columnLabels: { [key: string]: string } = {};
  allAvailableColumns: string[] = [];
  visibleColumns: { [key: string]: boolean } = {};
  sortBy: string | null = null;
  sortDirection: 'asc' | 'desc' = 'asc';
  private _displayedPositions: any[] = [];
  public get displayedPositions(): any[] { return this._displayedPositions; }
  public set displayedPositions(val: any[]) {
    this._displayedPositions = val ?? [];
    this.sortDisplayedPositions();
  }
  @Output() lastPositionsUpdated = new EventEmitter<any[]>();
  @ViewChild('scrollContainer') scrollContainer!: ElementRef<HTMLDivElement>;
  private scrollListener: (() => void) | null = null;
  availableUnits: { assetId: string; assetName: string }[] = [];
  selectedUnits: Set<string> = new Set();
  unitSearchText: string = '';
  private previousDisplayedPositions: any[] = [];
  currentTime: number = Date.now();
  private _ageTimer: any = null;
  private _processTimer: any = null;

  constructor(private datePipe: DatePipe) {
    console.log('🆕 [LastposicionComponent] Instanciado');
  }

  onClusterToggle(checked: boolean): void {
    try {
      this._clusteringEnabledInternal = !!checked;
      this.toggleClusteringEvent.emit(this._clusteringEnabledInternal);
    } catch (e) {
      // ignore
    }
  }

  ngOnInit(): void {
    this.initializeColumns();
    // iniciar timer para actualizar edades cada 30 segundos
    this._ageTimer = setInterval(() => {
      this.currentTime = Date.now();
    }, 30000);
  }

  ngOnDestroy(): void {
    if (this._ageTimer) {
      clearInterval(this._ageTimer);
      this._ageTimer = null;
    }
    this.removeScrollListener();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['lastPositions'] && this.lastPositions) {
      console.log('📍 [LastposicionComponent] lastPositions cambió:', this.lastPositions.length);
      this.scheduleProcessPositions();
      this.updateAvailableUnits();
      this.applyFilters(); // Aplicar filtros también cuando llegan nuevas posiciones
    }
    if (changes['alarms']) {
      console.log('🔍 [LastposicionComponent] alarms cambió:', {
        alarmsCount: this.alarms?.length,
        alarms: this.alarms,
        firstAlarm: this.alarms?.[0]
      });
      this.applyFilters();
      if (this.alarms && this.alarms.length > 0 && !this.scrollListener) {
        setTimeout(() => this.setupInfiniteScroll(), 200);
      }
    }
    if (changes['newAlarmAssetIds'] && (this.newAlarmAssetIds && this.newAlarmAssetIds.size > 0)) {
      // Reordenar para que activos con alarmas nuevas aparezcan primero
      this.reorderDisplayedByNewAlarms();
    }
      // debug logs removed
      // if (changes['alarmsByAssetId']) {
      //   // debug log removed
      // }
      // if (changes['isRiskEvaluation']) {
      //   // debug log removed
      // }
  }

  /** Debounce para evitar procesar posiciones en ráfaga */
  private scheduleProcessPositions(delay: number = 30): void {
    if (this._processTimer) clearTimeout(this._processTimer);
    this._processTimer = setTimeout(() => {
      this._processTimer = null;
      try {
        this.processPositions();
      } catch (e) {
        // ignore
      }
    }, delay);
  }

  /** Reordena displayedPositions colocando primero los assetIds que están en `newAlarmAssetIds` */
  private reorderDisplayedByNewAlarms(): void {
    if (!this.newAlarmAssetIds || this.newAlarmAssetIds.size === 0) return;

    const set = this.newAlarmAssetIds;
    this.displayedPositions.sort((a: any, b: any) => {
      const aNew = set.has(a.assetId) ? 1 : 0;
      const bNew = set.has(b.assetId) ? 1 : 0;
      if (aNew !== bNew) return bNew - aNew; // new alarms first
      // fallback to existing ordering: risk then timestamp
      if ((b.riskEvaluation || 0) !== (a.riskEvaluation || 0)) return (b.riskEvaluation || 0) - (a.riskEvaluation || 0);
      const at = a.reportTimestamp || 0;
      const bt = b.reportTimestamp || 0;
      return bt - at;
    });

    // Emitir cambio si hay diferencia
    if (JSON.stringify(this.displayedPositions) !== JSON.stringify(this.previousDisplayedPositions)) {
      this.previousDisplayedPositions = [...this.displayedPositions];
      this.lastPositionsUpdated.emit(this.displayedPositions);
    }
  }

  private initializeColumns(): void {
    const preferredKeys = [
      'assetId',
     
      'eventName'         
    
    ];

    this.allAvailableColumns = [
      'assetId',
      'assetName',
      'eventName',       
      'location',   
      'inputDate',
      'reportDate'
    ];

    this.columnLabels = {
      assetId: 'Asset ID',
      assetName: 'Nombre',
      eventName: 'Evento',
      latitude: 'Latitud',
      longitude: 'Longitud',
      location: 'Ubicación',
      speed: 'Velocidad',
      heading: 'Rumbo',
      inputDate: 'Fecha de Ingreso',
      reportDate:'Fecha de Reporte'
    };

    // Inicializar visibilidad: mostrar solo columnas con datos
    this.allAvailableColumns.forEach(col => {
      this.visibleColumns[col] = preferredKeys.includes(col) && this.displayedPositions.some(row => row[col] !== undefined && row[col] !== '');
    });

    this.updateVisibleColumns();
  }

  private processPositions(): void {
    // Normalizar/formatear valores (fecha)

    // Procesando posiciones (silenciado)
    this.displayedPositions = this.lastPositions.map((r: any) => {
      const reportRaw = r.reportDate ?? r.gpsReport?.reportDate ?? r.inputDate ?? null;
      const inputRaw = r.inputDate ?? null;
      const reportTimestamp = reportRaw ? new Date(reportRaw).getTime() : null;
      const riskVal = Number(r.riskEvaluation ?? r.gpsReport?.riskEvaluation ?? r.risk?.level ?? r.riskLevel ?? 0) || 0;

      // Detalles de posición procesada (silenciado)
      const riskLabelFromSource = r.riskLabel ?? r.gpsReport?.riskLabel ?? null;
      const derivedRiskLabel = (risk: number) => {
        if (risk >= 75) return 'Crítica';
        if (risk >= 50) return 'Alta';
        if (risk >= 25) return 'Media';
        if (risk > 0) return 'Baja';
        return 'Sin riesgo';
      };

      return {
        assetId: r.assetId ?? r.deviceId ?? '',
        assetName: r.assetName ?? r.gpsReport?.assetName ?? '',
        eventName: r.eventName ?? r.gpsReport?.eventName ?? '',
        latitude: r.latitude ?? r.gpsReport?.latitude ?? '',
        longitude: r.longitude ?? r.gpsReport?.longitude ?? '',
        location: r.location ?? r.gpsReport?.location ?? '',
        speed: r.speed ?? r.gpsReport?.speed ?? '',
        heading: r.heading ?? '',
        inputDate: inputRaw, // raw value; template will format with Date pipe
        reportDate: reportRaw, // raw value; template will format with Date pipe
        reportTimestamp,
        riskEvaluation: riskVal,
        color: r.color ?? (r.gpsReport?.color) ?? '#172c41',
        gpsReport: r.gpsReport ?? null,
        riskLabel: riskLabelFromSource ?? derivedRiskLabel(riskVal),
        zoneName: r.zoneName ?? r.gpsReport?.zoneName ?? ''
      };
    });

    // displayedPositions es ordenado automáticamente por su setter

    this.initializeColumns();

    // Emitir evento si cambió displayedPositions
    if (JSON.stringify(this.displayedPositions) !== JSON.stringify(this.previousDisplayedPositions)) {
      this.previousDisplayedPositions = [...this.displayedPositions];
      this.lastPositionsUpdated.emit(this.displayedPositions);
    }
  }

  /** Extraer lista de unidades únicas de displayedPositions (que vienen de lastPositions) */
  private updateAvailableUnits(): void {
    const seen = new Set<string>();
    const units: { assetId: string; assetName: string }[] = [];

    // Extraer de displayedPositions (son los datos procesados)
    (this.displayedPositions || []).forEach((pos: any) => {
      const id = pos.assetId || null;
      if (id && !seen.has(id)) {
        seen.add(id);
        units.push({ assetId: id, assetName: pos.assetName || id });
      }
    });

    // Si no hay displayedPositions, intentar extraer de lastPositions
    if (units.length === 0 && this.lastPositions && this.lastPositions.length > 0) {
      console.log('📋 Extrayendo unidades de lastPositions (displayedPositions vacío)');
      (this.lastPositions || []).forEach((pos: any) => {
        const id = pos.assetId || pos.deviceId || null;
        if (id && !seen.has(id)) {
          seen.add(id);
          units.push({ assetId: id, assetName: pos.assetName || id });
        }
      });
    }

    units.sort((a, b) => a.assetName.localeCompare(b.assetName));
    console.log('📋 Available Units:', units.length, units);
    this.availableUnits = units;
  }

  /** Ordena displayedPositions por `riskEvaluation` desc y `reportTimestamp` desc */
  private sortDisplayedPositions(): void {
    try {
      this._displayedPositions.sort((a: any, b: any) => {
        if ((b.riskEvaluation || 0) !== (a.riskEvaluation || 0)) return (b.riskEvaluation || 0) - (a.riskEvaluation || 0);
        const at = a.reportTimestamp || 0;
        const bt = b.reportTimestamp || 0;
        return bt - at;
      });
    } catch (e) {
      // ignore sort errors
    }
  }

  // Infinite scroll helpers (básicos, no modifican comportamiento actual sin template)
  private setupInfiniteScroll(): void {
    setTimeout(() => {
      const container = this.scrollContainer?.nativeElement;
      if (!container) return;

      this.scrollListener = () => this.onScroll(container as HTMLDivElement);
      container.addEventListener('scroll', this.scrollListener, { passive: true });
    }, 300);
  }

  private removeScrollListener(): void {
    if (this.scrollListener && this.scrollContainer?.nativeElement) {
      this.scrollContainer.nativeElement.removeEventListener('scroll', this.scrollListener);
      this.scrollListener = null;
    }
  }

  private onScroll(container: HTMLDivElement): void {
    const threshold = 100; // px antes del fondo para disparar carga
    const distanceToBottom = container.scrollHeight - container.scrollTop - container.clientHeight;

    // Emite evento genérico si se necesita cargar más (el padre puede escucharlo si lo expone)
    if (distanceToBottom <= threshold) {
      // placeholder: no tenemos emitter específico aquí
    }
  }

  get filteredUnits(): { assetId: string; assetName: string }[] {
    const text = this.unitSearchText.trim().toLowerCase();
    if (!text) return this.availableUnits;
    return this.availableUnits.filter(u =>
      u.assetId.toLowerCase().includes(text) || u.assetName.toLowerCase().includes(text)
    );
  }

  toggleUnit(assetId: string): void {
    if (this.selectedUnits.has(assetId)) {
      this.selectedUnits.delete(assetId);
    } else {
      this.selectedUnits.add(assetId);
    }
    this.applyFilters();
  }

  selectAllUnits(): void {
    this.filteredUnits.forEach(u => this.selectedUnits.add(u.assetId));
    this.applyFilters();
  }

  deselectAllUnits(): void {
    this.selectedUnits.clear();
    this.applyFilters();
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  applyFilters(): void {
    console.log('🎯 [LastposicionComponent.applyFilters] START:', {
      displayedPositionsCount: this.displayedPositions?.length,
      filterText: this.filterText,
      selectedUnitsCount: this.selectedUnits.size,
      hasActiveFilters: this.filterText.trim() !== '' || this.selectedUnits.size > 0
    });

    // Filtrar sobre displayedPositions (que vienen de lastPositions y están procesadas)
    if (!this.displayedPositions || this.displayedPositions.length === 0) {
      console.log('❌ No hay displayedPositions, vaciando filteredAlarms');
      this.filteredAlarms = [];
      this.filteredAssetIdsChange.emit(null);
      return;
    }

    let result = this.displayedPositions;
    const text = this.filterText.trim().toLowerCase();

    if (text) {
      console.log('🔎 Aplicando filtro de búsqueda:', text);
      result = result.filter(pos => {
        const assetId = (pos.assetId || '').toLowerCase();
        const assetName = (pos.assetName || '').toLowerCase();
        const eventName = (pos.eventName || '').toLowerCase();
        const location = (pos.location || '').toLowerCase();
        const matches = assetId.includes(text)
          || assetName.includes(text)
          || eventName.includes(text)
          || location.includes(text);
        return matches;
      });
      console.log('🔎 Después búsqueda:', result.length, 'posiciones');
    }

    if (this.selectedUnits.size > 0) {
      console.log('🚗 Aplicando filtro de unidades:', Array.from(this.selectedUnits));
      result = result.filter(pos => this.selectedUnits.has(pos.assetId));
      console.log('🚗 Después filtro unidades:', result.length, 'posiciones');
    }

    console.log('✅ Posiciones filtradas finales:', result.length, result);
    this.filteredAlarms = result;

    // Emitir IDs de assets filtrados para sincronizar markers del mapa
    if (this.hasActiveFilters) {
      const assetIds = [...new Set(result.map(p => p.assetId).filter(Boolean))];
      console.log('📍 Emitiendo assetIds filtrados:', assetIds);
      this.filteredAssetIdsChange.emit(assetIds);
    } else {
      console.log('📍 Sin filtros activos, emitiendo null (mostrar todos)');
      this.filteredAssetIdsChange.emit(null); // null = mostrar todos
    }
  }

  clearFilters(): void {
    this.filterText = '';
    this.selectedUnits.clear();
    this.unitSearchText = '';
    this.applyFilters();
  }

  get hasActiveFilters(): boolean {
    return this.filterText.trim() !== '' || this.selectedUnits.size > 0;
  }

  toggleColumnVisibility(column: string): void {
    this.visibleColumns[column] = !this.visibleColumns[column];
    this.updateVisibleColumns();
  }

  private updateVisibleColumns(): void {
    this.columns = this.allAvailableColumns.filter(col => this.visibleColumns[col]);
  }

  sortTable(column: string): void {
    if (this.sortBy === column) {
      // Toggle sort direction
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      // New column selected
      this.sortBy = column;
      this.sortDirection = 'asc';
    }

    this.displayedPositions.sort((a: any, b: any) => {
      const valueA = a[column];
      const valueB = b[column];

      if (valueA == null && valueB == null) return 0;
      if (valueA == null) return this.sortDirection === 'asc' ? 1 : -1;
      if (valueB == null) return this.sortDirection === 'asc' ? -1 : 1;

      let comparison = 0;
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        comparison = valueA.localeCompare(valueB);
      } else if (typeof valueA === 'number' && typeof valueB === 'number') {
        comparison = valueA - valueB;
      } else {
        comparison = String(valueA).localeCompare(String(valueB));
      }

      return this.sortDirection === 'asc' ? comparison : -comparison;
    });
  }

  onVehicleSelected(position: any): void {
    const latitude = parseFloat(position.latitude);
    const longitude = parseFloat(position.longitude);
    
    if (!isNaN(latitude) && !isNaN(longitude)) {
      this.vehicleSelected.emit({
        latitude,
        longitude,
        assetName: position.assetName || 'Vehículo',
        assetId: position.assetId || `${latitude}-${longitude}`
      });
    }
  }

  objectKeys(obj: any): string[] {
    return Object.keys(obj);
  }

  getTextColor(hex: string): string {
    if (!hex || hex.length !== 7) return 'black';
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    const luminosity = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminosity > 0.5 ? 'black' : 'white';
  }

  /**
   * Devuelve la diferencia entre ahora y `reportDate` en formato AA:MM:DD:HH:MM
   * AA = años (aprox. 365 días), MM = meses (aprox. 30 días), DD=días, HH=horas, MM=minutos
   * Los segundos se omiten.
   */
  public formatAge(reportDate: any, nowMs?: number): string {
    if (!reportDate) return '-';
    const d = new Date(reportDate);
    if (isNaN(d.getTime())) return '-';

    const now = nowMs ?? Date.now();
    let delta = Math.abs(Math.floor((now - d.getTime()) / 1000)); // segundos

    const years = Math.floor(delta / (365 * 24 * 3600));
    delta -= years * 365 * 24 * 3600;
    const months = Math.floor(delta / (30 * 24 * 3600));
    delta -= months * 30 * 24 * 3600;
    const days = Math.floor(delta / (24 * 3600));
    delta -= days * 24 * 3600;
    const hours = Math.floor(delta / 3600);
    delta -= hours * 3600;
    const minutes = Math.floor(delta / 60);

    const pad = (n: number) => String(n).padStart(2, '0');
    // partes en orden: años, meses, días, horas, minutos
    const parts = [pad(years), pad(months), pad(days), pad(hours), pad(minutes)];
    // sufijos solicitados: años -> 'a', meses -> 'm', días -> 'd', horas -> 'h', minutos -> 'm'
    const suffixes = ['A', 'M', 'd', 'h', 'm'];

    const labeled = parts.map((p, i) => `${p}${suffixes[i]}`);

    // eliminar ceros izquierdos pero garantizar al menos HHh MMm (últimos 2 segmentos)
    let firstNonZero = parts.findIndex(p => p !== '00');
    if (firstNonZero === -1) {
      // todos ceros -> 00h 00m
      return '00h 00m';
    }
    const minStart = Math.max(0, parts.length - 2);
    const start = Math.min(Math.max(firstNonZero, 0), minStart);
    return labeled.slice(start).join(' ');
  }

  /** True si alguna displayedPosition tiene edad > 30 minutos según currentTime */
  public hasStalePositions(): boolean {
    try {
      const threshold = 30 * 60 * 1000; // 30 minutos en ms
      const now = this.currentTime || Date.now();
      return (this.displayedPositions || []).some((p: any) => {
        const rt = p.reportTimestamp ?? (p.reportDate ? new Date(p.reportDate).getTime() : null);
        if (!rt) return false;
        return (now - rt) > threshold;
      });
    } catch (e) {
      return false;
    }
  }

  /** True si la posición `pos` tiene más de 30 minutos de antigüedad */
  public isStale(pos: any): boolean {
    try {
      const threshold = 30 * 60 * 1000; // 30 minutos en ms
      const now = this.currentTime || Date.now();
      const rt = pos?.reportTimestamp ?? (pos?.reportDate ? new Date(pos.reportDate).getTime() : null);
      if (!rt) return false;
      return (now - rt) > threshold;
    } catch (e) {
      return false;
    }
  }

}
